<template>
  <div
    :class="['tce-carousel-banner', { 'tce-carousel-banner--white': isWhite }]"
    role="group"
    :aria-roledescription="roleDescriptionDictionary"
  >
    <div ref="carouselBannerList">
      <slot />
    </div>
    <ul v-if="isMultiSlide" class="tce-carousel-banner__pagination">
      <li v-for="item in carouselLength" :key="item" class="tce-carousel-banner__pagination-item">
        <button
          :class="[
            'tce-carousel-banner__pagination-dot',
            { 'tce-carousel-banner__pagination-dot--active': isItemActive(item) },
          ]"
          :aria-current="isItemActive(item)"
          :aria-label="slideOf(item, carouselLength)"
          @click="changeSlide(item - 1)"
        >
          {{ slideOf(item, carouselLength) }}
        </button>
      </li>
    </ul>
    <div v-if="isMultiSlide" class="tce-carousel-banner__controls">
      <button @click="previousSlide">{{ previousLabel }}</button>
      <button @click="nextSlide">{{ nextLabel }}</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'tce-carousel-banner',
};
</script>

<script setup>
import { computed, onMounted, ref, watch, nextTick } from 'vue';

const SLIDE = {
  display: {
    active: '--active',
  },

  animation: {
    leaveLeft: '--leave-left',
    leaveRight: '--leave-right',
    enterLeft: '--enter-left',
    enterRight: '--enter-right',
  },

  visibility: {
    hidden: 'hidden',
    visible: 'visible',
  },
};

const MOBILE_SCREEN_END = 767;
let dragDistance = 0;
let timer = null;

const slots = ref(null);
const carouselLength = ref(null);
const isMultiSlide = ref(null);

const carouselBannerList = ref(null);
const activeSlideIndex = ref(0);
const transitionComplete = ref(true);
const isNext = ref(true);
let slides = ref(null);

const props = defineProps({
  isWhite: { type: Boolean, default: false },
  duration: { type: Number, default: 10 },
  roleDescriptionDictionary: { type: String, default: 'carousel' },
  previousLabel: { type: String, default: '' },
  nextLabel: { type: String, default: '' },
  paginationLabelStart: { type: String, default: '' },
  paginationLabelMiddle: { type: String, default: '' },
});

const isMobile = computed(() => {
  return window.innerWidth < MOBILE_SCREEN_END;
});

watch(activeSlideIndex, (newIndex, oldIndex) => {
  resetTimer();

  lazyLoad(newIndex);
  hideVideo(oldIndex);

  if (isNext.value) {
    slides.value[oldIndex]?.classList.add(SLIDE.animation.leaveLeft);
    slides.value[newIndex]?.classList.add(SLIDE.animation.enterRight);
  } else {
    slides.value[oldIndex]?.classList.add(SLIDE.animation.leaveRight);
    slides.value[newIndex]?.classList.add(SLIDE.animation.enterLeft);
  }

  slides.value[oldIndex].classList.remove(SLIDE.display.active);
  slides.value[newIndex].classList.add(SLIDE.display.active);

  slides.value[oldIndex].style.visibility = SLIDE.visibility.hidden;
  slides.value[newIndex].style.visibility = SLIDE.visibility.visible;

  autoplay();
});

function isItemActive(item) {
  return item - 1 === activeSlideIndex.value ? true : null;
}

function slideOf(item, carouselLength) {
  return `
    ${props.paginationLabelStart} ${item} ${props.paginationLabelMiddle} ${carouselLength}
  `;
}

function previousSlide() {
  if (!transitionComplete.value) return;
  transitionComplete.value = false;
  activeSlideIndex.value--;
  isNext.value = false;

  if (activeSlideIndex.value < 0) {
    activeSlideIndex.value = carouselLength.value - 1;
  }
}

function nextSlide() {
  if (!transitionComplete.value) return;
  transitionComplete.value = false;
  isNext.value = true;

  activeSlideIndex.value++;

  if (activeSlideIndex.value >= carouselLength.value) {
    activeSlideIndex.value = 0;
  }
}

function changeSlide(slideIndex) {
  if (activeSlideIndex.value === slideIndex) return;

  activeSlideIndex.value = slideIndex;
}

function handleTouchMove(e, dragValue) {
  if (dragValue < 0 && Math.abs(dragValue) >= 60) {
    previousSlide();
  } else if (dragValue > 0 && Math.abs(dragValue) >= 60) {
    nextSlide();
  }
}

function handleTouchStart(e) {
  if (!e.touches) {
    e.preventDefault();
  }

  dragDistance = 0;
  dragDistance = e.touches ? e.touches[0].pageX : e.pageX;
}

function handleTouchEnd(e) {
  if (!e.touches) {
    e.preventDefault();
  }

  dragDistance = dragDistance - (e.changedTouches ? e.changedTouches[0].pageX : e.pageX);

  if (!dragDistance && !e.touches) {
    if (e.target.closest('button')) {
      e.target.closest('button').click();
    }
    if (e.target.closest('a')) {
      e.target.closest('a').click();
    }
  }

  handleTouchMove(e, dragDistance);
}

function autoplay() {
  timer = setInterval(() => {
    nextSlide();
  }, props.duration * 1000);
}

function resetTimer() {
  clearTimeout(timer);
}

function lazyLoad(index) {
  const el = slides.value[index].children[0];
  if (el.localName === 'picture') {
    const sources = el.querySelectorAll('source');
    sources.forEach((source) => {
      const dataSrcset = source.getAttribute('data-srcset');
      if (dataSrcset) {
        source.srcset = dataSrcset;
      }
    });

    const img = el.querySelector('img');
    if (img) {
      const dataSrc = img.getAttribute('data-src');
      if (dataSrc) {
        img.src = dataSrc;
      }
    }

    return;
  }

  if (el.children[0].localName === 'video') {
    el.children[0].style.display = 'block';
  }
}

function hideVideo(index) {
  const el = slides.value[index].children[0];

  if (el.children[0].localName === 'video') {
    el.children[0].style.display = 'none';
  }
}

onMounted(async () => {
  await nextTick();

  const bannerList = carouselBannerList.value.querySelector('.tce-carousel-banner__list');

  slides.value = slots.value = bannerList.querySelectorAll('.tce-carousel-banner__slide');
  carouselLength.value = slots.value.length;
  isMultiSlide.value = carouselLength.value > 1;

  slides.value.forEach((slide) => (slide.style.visibility = SLIDE.visibility.hidden));

  slides.value[activeSlideIndex.value].classList.add(SLIDE.display.active);
  slides.value[activeSlideIndex.value].style.visibility = SLIDE.visibility.visible;

  lazyLoad(activeSlideIndex.value);

  slides.value.forEach((slide) => {
    slide.addEventListener('transitionend', () => {
      transitionComplete.value = true;
      slide.classList.remove(
        SLIDE.animation.leaveLeft,
        SLIDE.animation.leaveRight,
        SLIDE.animation.enterLeft,
        SLIDE.animation.enterRight,
      );
    });
  });

  if ('ontouchstart' in window && isMobile.value) {
    carouselBannerList.value.addEventListener('touchstart', handleTouchStart);
    carouselBannerList.value.addEventListener('touchend', handleTouchEnd);
  }

  autoplay();
});
</script>

<style lang="scss">
@import '../../styles/style.scss';
@import './style.scss';
</style>
